import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Leaf, ExternalLink } from "lucide-react";
import { whatsappUrl } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showWhatsappCTA, setShowWhatsappCTA] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    // restore session from localStorage
    const stored = localStorage.getItem("igreen_chat_session");
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data.session_id && data.messages) {
          setSessionId(data.session_id);
          setMessages(data.messages);
          setShowWhatsappCTA(!!data.showWhatsappCTA);
        }
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    // persist to localStorage
    if (sessionId) {
      localStorage.setItem(
        "igreen_chat_session",
        JSON.stringify({ session_id: sessionId, messages, showWhatsappCTA })
      );
    }
  }, [sessionId, messages, showWhatsappCTA]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const startSession = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(`${API}/chat/start`, { source: "website" });
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", content: data.greeting }]);
    } catch (e) {
      console.error(e);
      setMessages([
        {
          role: "assistant",
          content:
            "Ops, tive um problema para me conectar. Fale direto com o Rodney: (47) 99964-1269",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!sessionId) {
      startSession();
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading || !sessionId) return;

    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (data.should_redirect_whatsapp) {
        setShowWhatsappCTA(true);
      }
    } catch (e) {
      console.error(e);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Ops, tive um problema técnico. Fale direto com o Rodney no WhatsApp (47) 99964-1269 — ele vai te atender rapidinho!",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const resetChat = () => {
    localStorage.removeItem("igreen_chat_session");
    setSessionId(null);
    setMessages([]);
    setShowWhatsappCTA(false);
    startSession();
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={handleOpen}
          className="fixed bottom-5 right-5 z-[60] w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-2xl hover:scale-110 transition-transform grid place-items-center group"
          aria-label="Abrir chat"
        >
          <MessageCircle className="w-7 h-7" />
          <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-amber-400 text-black text-[10px] font-bold grid place-items-center animate-pulse">
            1
          </span>
          <span className="absolute right-full mr-3 whitespace-nowrap bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Fale com a SOL
          </span>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-4 right-4 z-[60] w-[calc(100vw-2rem)] max-w-sm h-[calc(100vh-2rem)] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 grid place-items-center">
              <Leaf className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm">SOL - iGreen Energy</div>
              <div className="text-emerald-100 text-xs flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span>
                Online agora
              </div>
            </div>
            <button
              onClick={resetChat}
              className="text-white/80 hover:text-white text-xs underline"
              title="Nova conversa"
            >
              Nova
            </button>
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/10 grid place-items-center"
              aria-label="Fechar chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-emerald-500 text-white rounded-br-sm"
                      : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "300ms" }}></span>
                  </div>
                </div>
              </div>
            )}

            {showWhatsappCTA && (
              <div className="mt-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 p-4 text-white">
                <div className="text-sm font-semibold">Pronto para falar com o Rodney?</div>
                <p className="text-xs text-emerald-50 mt-1">
                  Ele vai fazer sua simulação personalizada.
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-2 bg-white text-emerald-700 font-bold px-4 py-2 rounded-lg text-sm hover:bg-emerald-50 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> Abrir WhatsApp
                </a>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSend} className="p-3 border-t border-slate-200 bg-white flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={loading ? "Aguarde..." : "Digite sua mensagem..."}
              disabled={loading || !sessionId}
              className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 text-sm disabled:bg-slate-50"
            />
            <button
              type="submit"
              disabled={loading || !input.trim() || !sessionId}
              className="w-10 h-10 rounded-full bg-emerald-500 text-white grid place-items-center hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              aria-label="Enviar"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
